import { Component, OnDestroy } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule
} from '@angular/forms';
import { ViewWorkshopJobStoreService } from '../../view-workshop-job-store.service';
import { DialogRef } from '@angular/cdk/dialog';
import {
  WorkshopJobEventBase,
  WorkshopJobQuotationEvent,
  WorkshopJobEventDialogData,
  validateEventEdit
} from '../../../../models/workshop-job-event';
import { filter, map, mergeMap, take, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { WORKSHOP } from '../../../../../testIds';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { TestIdDirective } from '../../../../shared/directives/test-id.directive';
import { NgIf, AsyncPipe } from '@angular/common';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose
} from '@angular/material/dialog';
import { Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
  selector: 'app-quotation-dialog',
  templateUrl: './quotation-dialog.component.html',
  standalone: true,
  imports: [
    MatDialogTitle,
    ReactiveFormsModule,
    MatDialogContent,
    NgIf,
    TestIdDirective,
    MatFormField,
    MatLabel,
    MatInput,
    CdkTextareaAutosize,
    MatCheckbox,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    AsyncPipe
  ]
})
export class QuotationDialogComponent implements OnDestroy {
  testIds = WORKSHOP;
  isEditing = false;

  form = new FormGroup({
    work_required: new FormControl<string>(null, Validators.required),
    estimated_total_cost: new FormControl<number>(null),
    send_email: new FormControl<boolean>(false),
    email_address: new FormControl<string>(null, Validators.email)
  });

  private currentJobStatus$ = this.store.job$.pipe(
    map((job) => job.work_status)
  );

  private validationResult$ = this.currentJobStatus$.pipe(
    map((status) => validateEventEdit('quotation', status))
  );

  canEdit$ = this.validationResult$.pipe(map((result) => result.canEdit));
  validationMessage$ = this.validationResult$.pipe(
    map((result) => result.validationMessage)
  );

  lastAssessmentEvent$ = this.store.eventsOrdered$.pipe(
    map((events) => events.find((event) => event.type === 'assessment'))
  );
  private destroy$ = new Subject<void>();

  constructor(
    private store: ViewWorkshopJobStoreService,
    private dialog: DialogRef<QuotationDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: WorkshopJobEventDialogData
  ) {
    this.isEditing = !!this.data?.eventToEdit;

    if (this.isEditing) {
      this.canEdit$.pipe(take(1)).subscribe((canEdit) => {
        if (!canEdit) {
          this.form.disable();
        }
      });
    }

    this.store.customer$
      .pipe(
        takeUntil(this.destroy$),
        filter(Boolean),
        map((customer) => customer.email),
        mergeMap((email) =>
          this.form.controls.send_email.valueChanges.pipe(
            tap((send_email) => {
              const emailControl = this.form.controls.email_address;
              if (send_email) {
                emailControl.setValidators([
                  Validators.required,
                  Validators.email
                ]);
                emailControl.patchValue(email ? email : null);
              } else {
                emailControl.setValidators([Validators.email]);
                emailControl.patchValue(null);
              }
            })
          )
        )
      )
      .subscribe();
  }

  saveQuotation() {
    const { work_required, estimated_total_cost, send_email, email_address } =
      this.form.value;
    const event: Partial<WorkshopJobQuotationEvent> = {
      type: 'quotation',
      details: {
        work_required,
        send_email,
        email_address: send_email ? email_address : null,
        estimated_total_cost: estimated_total_cost
          ? estimated_total_cost * 100
          : null
      }
    };
    this.store.addEvent({
      jobEvent: event,
      onSuccess: () => this.dialog.close()
    });
  }

  prefill(assessment: WorkshopJobEventBase<any>) {
    let work_required =
      (this.form.value.work_required?.length ?? 0 > 0)
        ? this.form.controls.work_required.value + '\n'
        : '';
    work_required += assessment.details.assessment;
    this.form.patchValue({
      work_required,
      estimated_total_cost: assessment.details.estimated_total_cost
        ? assessment.details.estimated_total_cost / 100
        : null
    });
  }

  get dialogTitle(): string {
    return this.isEditing ? 'Edit Quotation for Job' : 'Add Quotation for Job';
  }

  get saveButtonText(): string {
    return this.isEditing ? 'Update' : 'Add';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
